import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Building2, CalendarDays, Home, ArrowUpRight } from "lucide-react";

const Placeholder = ({ name }) => (
  <div className="aspect-[16/10] w-full flex flex-col items-center justify-center bg-[#1C2220] text-white rounded-sm">
    <span className="font-heading text-6xl md:text-7xl text-[#E8B4A0]">{name.charAt(0)}</span>
    <span className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/50">Photography coming soon</span>
  </div>
);

export const Portfolio = ({ properties = [] }) => {
  const [activeSlug, setActiveSlug] = useState(null);

  useEffect(() => {
    if (properties.length && !activeSlug) setActiveSlug(properties[0].slug);
  }, [properties, activeSlug]);

  const active = properties.find((p) => p.slug === activeSlug) || properties[0];
  const location = (p) => [p.city, p.county, p.state].filter(Boolean).join(", ");

  return (
    <section id="communities" data-testid="portfolio-section" className="relative bg-[#EEEAE0] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-20">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C56A4E] mb-6">Our Portfolio</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1C2220] max-w-3xl">
              Quality Renovations. <span className="italic text-[#C56A4E]">Lasting Affordability.</span>
            </h2>
          </div>
          <p className="text-sm text-[#1C2220]/50 max-w-xs">
            A growing portfolio of affordable housing communities across North Carolina
            and South Carolina, preserved and improved for the long term. Select a
            community to open its photos and details.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Clickable list */}
          <div className="lg:col-span-5" data-testid="property-list">
            <div className="border-t border-[#1C2220]/15">
              {properties.length === 0 && (
                <div data-testid="properties-loading" className="py-8 text-sm text-[#1C2220]/50">
                  Loading communities…
                </div>
              )}
              {properties.map((p) => {
                const isActive = p.slug === activeSlug;
                return (
                  <button key={p.slug} onClick={() => setActiveSlug(p.slug)}
                    data-testid={`property-item-${p.slug}`}
                    className="w-full text-left border-b border-[#1C2220]/15 py-5 group flex items-center justify-between gap-4">
                    <div className="flex items-baseline gap-4 min-w-0">
                      <span className={`font-heading text-xl md:text-2xl transition-colors duration-300 truncate ${
                        isActive ? "text-[#C56A4E]" : "text-[#1C2220] group-hover:text-[#C56A4E]"}`}>
                        {p.name}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#1C2220]/40 whitespace-nowrap">
                        {p.city}, {p.state}
                      </span>
                    </div>
                    <ArrowUpRight size={18} className={`shrink-0 transition-all duration-300 ${
                      isActive ? "text-[#C56A4E] opacity-100" : "text-[#1C2220]/40 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail viewer */}
          <div className="lg:col-span-7 lg:sticky lg:top-28 self-start" data-testid="property-detail">
            <AnimatePresence mode="wait">
              {active && (
                <motion.div key={active.slug} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}>
                  {/* Photos */}
                  {active.images.length > 0 ? (
                    <>
                      <div className="relative overflow-hidden rounded-sm">
                        <div className="aspect-[16/10] overflow-hidden">
                          <motion.img key={active.images[0]} src={active.images[0]} alt={active.name}
                            initial={{ scale: 1.06 }} animate={{ scale: 1 }}
                            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                            className="h-full w-full object-cover" />
                        </div>
                        <span className="absolute top-4 left-4 bg-[#1C2220] text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm">
                          {active.community_type}
                        </span>
                        {active.captions?.[0] && (
                          <span className="absolute bottom-3 right-3 bg-black/40 backdrop-blur-sm text-white/90 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-sm">
                            {active.captions[0]}
                          </span>
                        )}
                      </div>
                      {active.images.length > 1 && (
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          {active.images.slice(1, 3).map((src, i) => (
                            <div key={src} className="relative aspect-[16/11] overflow-hidden rounded-sm">
                              <img src={src} alt={`${active.name} photo ${i + 2}`} className="h-full w-full object-cover" />
                              {active.captions?.[i + 1] && (
                                <span className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm text-white/90 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm">
                                  {active.captions[i + 1]}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Placeholder name={active.name} />
                  )}

                  {/* Details */}
                  <div className="mt-8">
                    <h3 className="font-heading text-3xl md:text-4xl text-[#1C2220] tracking-tight">{active.name}</h3>

                    <dl className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4 border-t border-[#1C2220]/15 pt-6">
                      {active.address && (
                        <div className="flex gap-3">
                          <MapPin size={16} className="text-[#C56A4E] mt-0.5 shrink-0" />
                          <div>
                            <dt className="text-[10px] uppercase tracking-[0.2em] text-[#1C2220]/45">Address</dt>
                            <dd className="text-sm text-[#1C2220]/80">{active.address}<br />{location(active)}</dd>
                          </div>
                        </div>
                      )}
                      {active.ptype && (
                        <div className="flex gap-3">
                          <Home size={16} className="text-[#C56A4E] mt-0.5 shrink-0" />
                          <div>
                            <dt className="text-[10px] uppercase tracking-[0.2em] text-[#1C2220]/45">Type</dt>
                            <dd className="text-sm text-[#1C2220]/80">{active.ptype}</dd>
                          </div>
                        </div>
                      )}
                      {active.vintage && (
                        <div className="flex gap-3">
                          <CalendarDays size={16} className="text-[#C56A4E] mt-0.5 shrink-0" />
                          <div>
                            <dt className="text-[10px] uppercase tracking-[0.2em] text-[#1C2220]/45">Vintage</dt>
                            <dd className="text-sm text-[#1C2220]/80">{active.vintage}</dd>
                          </div>
                        </div>
                      )}
                      {active.units > 0 && (
                        <div className="flex gap-3">
                          <Building2 size={16} className="text-[#C56A4E] mt-0.5 shrink-0" />
                          <div>
                            <dt className="text-[10px] uppercase tracking-[0.2em] text-[#1C2220]/45">Units</dt>
                            <dd className="text-sm text-[#1C2220]/80">{active.units}</dd>
                          </div>
                        </div>
                      )}
                    </dl>

                    <p className="mt-6 text-base leading-relaxed text-[#1C2220]/75 max-w-2xl">{active.description}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
